import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Camera, 
  Wand2, 
  Download, 
  Shield, 
  Zap, 
  Image,
  CheckCircle,
  Star,
  ArrowRight,
  Sparkles,
  Users,
  Clock,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function HowItWorks() {
  const { language, isRTL } = useLanguage();

  const steps = language === 'he' ? [
    {
      num: 1,
      icon: Wand2,
      title: 'בחרו דמות',
      description: 'גלו את הגלריה שלנו עם למעלה מ-80 דמויות מגוונות - מגיבורי-על ועד מקצועות, מפנטזיה ועד היסטוריה. יש דמות לכל אחד!'
    },
    {
      num: 2,
      icon: Camera,
      title: 'צלמו תמונה',
      description: 'השתמשו במצלמה של המכשיר שלכם או העלו תמונה קיימת. הטכנולוגיה שלנו עובדת הכי טוב עם תמונות פנים ברורות ותאורה טובה.'
    },
    {
      num: 3,
      icon: Zap,
      title: 'הקסם קורה',
      description: 'האלגוריתם המתקדם שלנו שומר על הפנים שלכם בדיוק מלא תוך שינוי הגוף, הבגדים והרקע להתאמה מושלמת לדמות.'
    },
    {
      num: 4,
      icon: Download,
      title: 'שתפו והורידו',
      description: 'קבלו את התמונה שלכם באיכות גבוהה תוך שניות! הורידו, שתפו עם חברים, או צרו עוד ועוד.'
    }
  ] : [
    {
      num: 1,
      icon: Wand2,
      title: 'Choose a Character',
      description: 'Explore our gallery with over 80 diverse characters - from superheroes to professionals, from fantasy to history. There\'s a character for everyone!'
    },
    {
      num: 2,
      icon: Camera,
      title: 'Take a Photo',
      description: 'Use your device\'s camera or upload an existing photo. Our technology works best with clear face photos and good lighting.'
    },
    {
      num: 3,
      icon: Zap,
      title: 'Magic Happens',
      description: 'Our advanced algorithm preserves your face exactly while transforming the body, clothing, and background to perfectly match the character.'
    },
    {
      num: 4,
      icon: Download,
      title: 'Share & Download',
      description: 'Get your high-quality image in seconds! Download, share with friends, or create more and more.'
    }
  ];

  const features = language === 'he' ? [
    { icon: Shield, title: 'שמירה על הפנים', description: 'הטכנולוגיה שלנו שומרת על תווי הפנים שלכם בדיוק מושלם' },
    { icon: Clock, title: '15 שניות בלבד', description: 'קבלו תוצאות מדהימות תוך שניות ספורות' },
    { icon: Image, title: 'איכות 8K', description: 'תמונות באיכות מקצועית מוכנות להדפסה ושיתוף' },
    { icon: Users, title: '80+ דמויות', description: 'מגוון ענק של דמויות מכל הקטגוריות' }
  ] : [
    { icon: Shield, title: 'Face Preservation', description: 'Our technology preserves your facial features with perfect accuracy' },
    { icon: Clock, title: 'Only 15 Seconds', description: 'Get amazing results in just a few seconds' },
    { icon: Image, title: '8K Quality', description: 'Professional quality images ready for printing and sharing' },
    { icon: Users, title: '80+ Characters', description: 'Huge variety of characters from all categories' }
  ];

  const faqs = language === 'he' ? [
    { q: 'האם השירות בטוח?', a: 'בהחלט. התמונות שלכם מאובטחות ולא משותפות עם אף אחד. אתם שולטים במה שקורה עם התמונות שלכם.' },
    { q: 'כמה זמן לוקח ליצור תמונה?', a: 'בדרך כלל 15-30 שניות. התהליך מהיר ואוטומטי לחלוטין.' },
    { q: 'האם אני יכול להשתמש בתמונות למטרות מסחריות?', a: 'עם חבילת Pro שכוללת רישיון מסחרי - כן! חבילות אחרות הן לשימוש אישי בלבד.' },
    { q: 'מה קורה אם התוצאה לא טובה?', a: 'נסו שוב! לפעמים תמונת מקור שונה או זווית אחרת נותנות תוצאות טובות יותר. אם יש בעיה - פנו לתמיכה.' },
    { q: 'האם יש הגבלה על מספר היצירות?', a: 'לא! כל עוד יש לכם קרדיטים, אתם יכולים ליצור כמה תמונות שתרצו.' }
  ] : [
    { q: 'Is the service safe?', a: 'Absolutely. Your photos are secure and not shared with anyone. You control what happens with your images.' },
    { q: 'How long does it take to create an image?', a: 'Usually 15-30 seconds. The process is fast and fully automatic.' },
    { q: 'Can I use the images commercially?', a: 'With the Pro package that includes a commercial license - yes! Other packages are for personal use only.' },
    { q: 'What if the result isn\'t good?', a: 'Try again! Sometimes a different source photo or angle gives better results. If there\'s an issue - contact support.' },
    { q: 'Is there a limit on the number of creations?', a: 'No! As long as you have credits, you can create as many images as you want.' }
  ];

  const testimonials = language === 'he' ? [
    { name: 'דני כהן', text: 'מדהים! הפכתי את עצמי לספיידרמן והילדים התלהבו', rating: 5 },
    { name: 'מיכל לוי', text: 'השתמשתי באירוע חברה והיה סנסציה', rating: 5 },
    { name: 'יוסי אברהם', text: 'איכות התמונות פשוט מושלמת', rating: 5 }
  ] : [
    { name: 'John Smith', text: 'Amazing! I turned myself into Spider-Man and the kids loved it', rating: 5 },
    { name: 'Sarah Johnson', text: 'Used it at a company event and it was a sensation', rating: 5 },
    { name: 'Mike Wilson', text: 'The image quality is simply perfect', rating: 5 }
  ];

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-violet-400">
              {language === 'he' ? 'טכנולוגיית AI מתקדמת' : 'Advanced AI Technology'}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {language === 'he' ? 'איך זה עובד?' : 'How It Works'}
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            {language === 'he'
              ? 'גלו איך הטכנולוגיה שלנו הופכת אתכם לכל דמות שתרצו תוך שמירה מושלמת על הפנים שלכם'
              : 'Discover how our technology transforms you into any character while perfectly preserving your face'}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="mb-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="glass-card rounded-2xl p-6 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center mb-4">
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                    <span className="text-sm font-bold text-violet-400">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {language === 'he' ? 'למה לבחור בנו?' : 'Why Choose Us?'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="glass-card rounded-xl p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-pink-600/20 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {language === 'he' ? 'מה אומרים עלינו' : 'What People Say'}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="glass-card rounded-xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-4">"{testimonial.text}"</p>
                <p className="text-sm text-violet-400 font-medium">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {language === 'he' ? 'שאלות נפוצות' : 'Frequently Asked Questions'}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="glass-card rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  {faq.q}
                </h3>
                <p className="text-slate-400 ms-8">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="glass-card rounded-3xl p-12 bg-gradient-to-br from-violet-600/10 to-pink-600/10 border-violet-500/20">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'he' ? 'מוכנים להתחיל?' : 'Ready to Start?'}
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
              {language === 'he'
                ? 'הצטרפו עכשיו וקבלו 5 קרדיטים חינם ליצירת התמונות הראשונות שלכם'
                : 'Join now and get 5 free credits to create your first images'}
            </p>
            <Link to={createPageUrl('Photobooth')}>
              <Button size="lg" className="bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white px-12 py-6 text-lg rounded-xl">
                <Camera className="w-5 h-5 me-2" />
                {language === 'he' ? 'התחל עכשיו' : 'Start Now'}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
